import Vue from 'vue'
import stream from 'stream'

// New format: log is an array of objects (message, log_level, timestamp, exception, job_prefix, etc.) → one Buffer per entry, JSON-encoded
// Legacy format: log is a string (backend-formatted logs) → split by newlines, one Buffer per line
// Shared by every service-specific LogStream (DPE, DM, ML, ...) so the JSON/legacy split lives in one place.
export function logEntriesToBuffers (log) {
  const isArrayOfObjects = Array.isArray(log) && log.length
  if (isArrayOfObjects) {
    return log.map(entry => Buffer.from(JSON.stringify(entry), 'utf-8'))
  }
  const text = typeof log === 'string' ? log : String(log)
  return text.split('\n').map(line => Buffer.from(line, 'utf-8'))
}

class LogStream extends stream.Readable {
  // `options` lets each service tune the parts of the WebSocket contract that differ
  // between them, while sharing the JSON/legacy parsing and the stream plumbing:
  //   - channelKey:      value used in the `<service>.logs.<key>` subscribe channel
  //                      (DPE keys on the jobId; DWH/DM keys on the job_type).
  //   - subscribeData:   payload sent with `logs_subscribe`.
  //   - unsubscribeData: payload sent with `logs_unsubscribe` on close().
  // The defaults reproduce the original DPE contract, so existing callers are unchanged.
  constructor (jobId, execId, service, serviceType = '', options = {}) {
    super()
    this.jobId = jobId
    this.service = service
    this.execId = execId
    this.ended = false
    this.serviceType = serviceType
    this.name = serviceType.toUpperCase()
    this.channelKey = options.channelKey ?? jobId
    this.subscribeData = options.subscribeData ?? { job_id: jobId, execution_id: execId, service }
    this.unsubscribeData = options.unsubscribeData ?? { job_id: jobId, execution_id: execId, service }
    // Single source of truth for the channel so subscribe/unsubscribe can never drift apart.
    const execSuffix = this.execId ? `.${this.execId}` : ''
    this.channel = `${serviceType.toLowerCase()}.logs.${this.channelKey}${execSuffix}`
    // Resume support: remember the last timestamp we delivered so a reconnect can
    // resume from there. While `resumeFrom` is set we drop replayed lines the
    // client already has (dedup by timestamp — O(1) memory, robust to a full replay).
    this.lastSeen = null
    this.resumeFrom = null
    // Always-on dedup: bounded FIFO of recent line signatures (timestamp|message)
    // to drop exact duplicates from ANY source — reconnect replay, a racy double
    // subscribe, backend boundary re-fetch. JSON mode only (text lines may repeat).
    this._seenSigs = new Set()
    this._seenSigOrder = []
    this.onMessage = this.onMessage.bind(this)
    this._resume = this._resume.bind(this)
    Vue.WebSockets.subscribe(this.channel, this.onMessage)
    if (Vue.WebSockets.onReconnect) Vue.WebSockets.onReconnect(this.name, this._resume)
    this._sendSubscribe()
  }

  _sendSubscribe (since = null) {
    Vue.WebSockets.send(this.name, { method: 'logs_subscribe', data: { ...this.subscribeData, since } })
  }

  // Re-subscribe after a websocket reconnect, resuming from the last line we
  // received so the server doesn't replay the whole job.
  _resume () {
    if (this.ended) return
    this.resumeFrom = this.lastSeen
    this._sendSubscribe(this.lastSeen)
  }

  // First time a signature is seen -> true; repeats -> false.
  // Bounded FIFO window keeps memory flat on long streams.
  _admitOnce (sig) {
    if (this._seenSigs.has(sig)) return false
    this._seenSigs.add(sig)
    this._seenSigOrder.push(sig)
    if (this._seenSigOrder.length > 2000) this._seenSigs.delete(this._seenSigOrder.shift())
    return true
  }

  onMessage ({ data }) {
    if (this.ended || data?.log == null) return
    const log = data.log
    // New format: data.log is an array of objects (message, log_level, timestamp, ...)
    const isArrayOfObjects = Array.isArray(log) && log.length
    if (isArrayOfObjects) {
      log.forEach(entry => {
        const ts = entry != null ? entry.timestamp : null
        // After a reconnect, skip lines the client already displayed.
        if (this.resumeFrom != null && ts != null) {
          if (ts <= this.resumeFrom) return
          this.resumeFrom = null // caught up with new lines, stop filtering
        }
        // Drop exact duplicates (same timestamp + message), whatever the cause.
        if (ts != null && !this._admitOnce(`${ts}|${entry.message}`)) return
        if (ts != null && (this.lastSeen == null || ts > this.lastSeen)) this.lastSeen = ts
        // Push full entry as JSON so the console can use message, log_level, timestamp, exception, job_prefix
        this.push(Buffer.from(JSON.stringify(entry), 'utf-8'))
      })
    } else {
      // Legacy format: string → one buffer per line.
      logEntriesToBuffers(log).forEach(buf => this.push(buf))
    }
  }

  /* istanbul ignore next */
  _read () {
  }

  close () {
    Vue.WebSockets.send(this.name, { method: 'logs_unsubscribe', data: this.unsubscribeData })
    Vue.WebSockets.unsubscribe(this.channel, this.onMessage)
    if (Vue.WebSockets.offReconnect) Vue.WebSockets.offReconnect(this.name, this._resume)
    this.ended = true
  }
}

export default LogStream
